import { applyWSSHandler } from '@trpc/server/adapters/ws';
import getPort from 'get-port';
import { WebSocketServer } from 'ws';
import { createHTTPServer } from './httpAdapter.js';
import cors from 'cors';
import { getDomain } from '@ms-cloudpack/create-express-app';
import type { Context } from '../types/Context.js';
import * as apis from '../apis/index.js';
import { createRouter } from './common.js';
import type { AppRouter } from '../types/AppRouter.js';

export interface CloudpackServer extends Pick<WebSocketServer, 'clients'> {
  /** Full server root URL, including protocol and port */
  url: string;
  /** Close the server. Does nothing if called multiple times. */
  close: () => Promise<void>;
}

export const apiServerPort = [9890, 9891, 9892, 9893];

/**
 * Creates a cloudpack server that can be used to communicate with the cloudpack client using trpc.
 */
export async function createCloudpackServer(
  options: {
    /** Custom port(s) to try for the API server. */
    port?: number | number[];
  },
  context: Context,
): Promise<CloudpackServer> {
  const portArg = Array.isArray(options.port) ? options.port : options.port ? [options.port] : undefined;
  const port = await getPort({ port: portArg || apiServerPort });
  if (portArg && !portArg.includes(port)) {
    throw new Error(`No ports from range ${JSON.stringify(portArg)} are available`);
  }

  const createContext = () => Promise.resolve(context);
  const hostname = getDomain(context.session.config?.server?.domain);
  const https = context.session.config?.server?.parsedHttps ?? context.session.config?.server?.https;

  const procedures = Object.fromEntries(Object.entries(apis).map(([name, api]) => [name, api.procedure]));
  // AppRouter is the correct type, but doesn't get inferred correctly after Object.entries
  const router = createRouter(procedures) as unknown as AppRouter;

  const { server, listen } = await createHTTPServer({
    domain: hostname,
    middleware: cors(),
    router,
    createContext,
    https,
    cwd: context.session.config.appPath,
    // This happens when an error is thrown internally in a procedure, and possibly other cases
    // (but not for subscription data validation errors).
    onError: (err) => {
      console.debug(
        [
          `Error in Cloudpack API server ${err.path}:`,
          `Input: ${JSON.stringify(err.input, null, 2)}`,
          `Error: ${err.error.stack || err.error.message}`,
          `Details: ${JSON.stringify(err.error, null, 2)}`,
        ].join('\n'),
      );
    },
  });

  await listen(port, hostname);

  const wss = new WebSocketServer({ server });

  applyWSSHandler<AppRouter>({
    wss,
    router,
    createContext,
  });

  const webSocketProtocol = https ? 'wss' : 'ws';
  const url = `${webSocketProtocol}://${hostname}:${port}`;
  context.session.urls.apiServer = url;

  let closed = false;

  return {
    url,
    close: async () => {
      if (closed) return;
      closed = true;

      await new Promise<void>((resolve, reject) => {
        wss.clients.forEach((socket) => socket.terminate());
        wss.close((err) => (err ? reject(err) : resolve()));
        server.close();
      });
    },
    get clients() {
      return wss.clients;
    },
  };
}
